/**
 * Main App Component
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ErrorBoundary } from '@components/common';
import { useAlertsStore } from '@store/index';
import Alert from '@components/common/Alert';
import { AuthProvider } from './context/AuthContext';
import { SupportProvider } from './context/SupportContext';
import Layout from './components/Layout';
import Login from './components/Login';
import HomePage from './pages/HomePage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import CasesPage from './pages/CasesPage';
import DownloadsPage from './pages/DownloadsPage';
import PageLayout from './components/PageLayout';
import LearningPage from './pages/LearningPage';
import DocumentationPage from './pages/DocumentationPage';
import CommunityPage from './pages/CommunityPage';
import TestHome from './pages/TestHome';
import SSOFinalPage from './pages/SSOFinalPage';
import SignOutCallbackPage from './pages/SignOutCallbackPage';
import ProtectedRoute from './components/ProtectedRoute';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import KnowledgeBaseLaunchPage from './pages/KnowledgeBaseLaunchPage';
import ArticlePage from './pages/ArticlePage';
import '@/App.css';

const App: React.FC = () => {
  const { alerts, removeAlert } = useAlertsStore();

  // Lazy load remotes
  // Note: These must match the keys in vite.config.ts remotes configuration
  const RemoteDashboard = React.lazy(() => import('dashboard/App').catch(() => ({ default: () => <div>Dashboard Unavailable</div> })));
  const RemoteProfile = React.lazy(() => import('profile/App').catch(() => ({ default: () => <div>Profile Unavailable</div> })));
  const RemoteCases = React.lazy(() => import('cases/App').catch(() => ({ default: () => <div>Cases Unavailable</div> })));
  const RemoteDownloads = React.lazy(() => import('downloads/App').catch(() => ({ default: () => <div>Downloads Unavailable</div> })));
  const RemoteKnowledge = React.lazy(() => import('knowledge/App').catch(() => ({ default: () => <div>Knowledge Unavailable</div> })));

  return (
    <ErrorBoundary>
      <AuthProvider>
        <SupportProvider>
          <Router>
            <div className="app">
              <div className="app__alerts">
                {alerts.map((alert) => (
                  <Alert
                    key={alert.id}
                    alert={alert}
                    onClose={() => removeAlert(alert.id)}
                  />
                ))}
              </div>

              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signin-oidc" element={<SSOFinalPage />} />
                <Route path="/signout-callback-oidc" element={<SignOutCallbackPage />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <Layout>
                        <React.Suspense fallback={<div className="p-4">Loading Micro-Frontend...</div>}>
                          <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/test" element={<TestHome />} />

                            {/* Profile Remote */}
                            <Route path="/profile" element={
                              <ErrorBoundary>
                                <RemoteProfile />
                              </ErrorBoundary>
                            } />

                            <Route path="/support" element={<SupportPage />} />

                            {/* Cases Remote */}
                            <Route path="/cases" element={
                              <ErrorBoundary>
                                <RemoteCases />
                              </ErrorBoundary>
                            } />

                            {/* Downloads Remote */}
                            <Route path="/downloads" element={
                              <ErrorBoundary>
                                <RemoteDownloads />
                              </ErrorBoundary>
                            } />

                            <Route path="/knowledge" element={<KnowledgeBasePage />} />
                            <Route path="/knowledge/launch" element={<KnowledgeBaseLaunchPage />} />
                            <Route path="/knowledge/article/:id" element={<ArticlePage />} />

                            {/* Knowledge Remote (Keep as fallback if needed for other paths) */}
                            <Route path="/knowledge-mfe/*" element={
                              <PageLayout>
                                <ErrorBoundary>
                                  <RemoteKnowledge />
                                </ErrorBoundary>
                              </PageLayout>
                            } />

                            <Route path="/learning" element={<LearningPage />} />
                            <Route path="/documentation" element={<DocumentationPage />} />
                            <Route path="/community" element={<CommunityPage />} />

                            {/* Dashboard Remote Route (Optional usage) */}
                            <Route path="/dashboard-test" element={
                              <ErrorBoundary>
                                <RemoteDashboard />
                              </ErrorBoundary>
                            } />

                            <Route path="*" element={<div>Page Not Found</div>} />
                          </Routes>
                        </React.Suspense>
                      </Layout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </div>
          </Router>
        </SupportProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
